import { ActionMenuItem } from '@seahorse/ui';

/** A segmented preset shown in tier 1 (design handoff, "Step 4, streamlined", section 2). */
export interface PageView {
  key: string;
  /** Translation key. */
  label: string;
  count?: number | null;
}

/**
 * A tier-1 action. Extends `ActionMenuItem` (the same item model
 * `sh-action-menu` uses) so configuration-driven pages can map their
 * switches onto one shape for both the header and the overflow menu.
 * `menu`, when set, turns the action into a button with a chevron that
 * opens a popup `p-menu` (e.g. "Export ▾").
 * `variant: 'text'` renders a secondary action as a plain text button
 * (e.g. "Cancel" next to a primary "Save") instead of an outlined one.
 */
export interface PageAction<T = void> extends ActionMenuItem<T> {
  menu?: ActionMenuItem<T>[];
  variant?: 'text';
}

export type PageFilterType =
  | 'select'
  | 'multiselect'
  | 'text'
  | 'boolean'
  | 'date'
  | 'dateRange';

export interface PageFilterOption {
  /** Display text, already translated or plain -- not a translation key. */
  label: string;
  value: unknown;
}

export interface PageFilter {
  key: string;
  /** Translation key. */
  label: string;
  type: PageFilterType;
  /** For `select` / `multiselect`. */
  options?: PageFilterOption[];
  /** undefined/null/''/[] means inactive. */
  value?: unknown;
}

export type PageFilterValues = Record<string, unknown>;

/** A filter with no value, an empty string or an empty array is inactive. */
export function isFilterActive(filter: PageFilter): boolean {
  const value = filter.value;
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (filter.type === 'dateRange' && Array.isArray(value))
    return (value as unknown[]).length > 0;
  return true;
}

/** English fallback labels for the boolean formatter (a pure function has no TranslateService). */
export interface BooleanLabels {
  yes: string;
  no: string;
}

const DEFAULT_BOOLEAN_LABELS: BooleanLabels = { yes: 'Yes', no: 'No' };
const MAX_MULTISELECT_LABELS = 2;

/**
 * Formats a filter's active value for the "Field value" link (design
 * handoff, "Step 4, streamlined", section 3): option labels for
 * select/multiselect (more than two items collapse to "a, b +n"), Yes/No
 * for boolean, and a formatted date or "from – to" for dates.
 */
export function formatFilterValue(
  filter: PageFilter,
  locale = 'en',
  booleanLabels: BooleanLabels = DEFAULT_BOOLEAN_LABELS
): string {
  const value = filter.value;

  switch (filter.type) {
    case 'select': {
      const option = filter.options?.find((o) => o.value === value);
      return option?.label ?? String(value ?? '');
    }
    case 'multiselect': {
      const values = Array.isArray(value) ? value : [];
      const labels = values.map(
        (v) => filter.options?.find((o) => o.value === v)?.label ?? String(v)
      );
      if (labels.length <= MAX_MULTISELECT_LABELS) return labels.join(', ');
      const shown = labels.slice(0, MAX_MULTISELECT_LABELS);
      return `${shown.join(', ')} +${labels.length - MAX_MULTISELECT_LABELS}`;
    }
    case 'boolean':
      return value ? booleanLabels.yes : booleanLabels.no;
    case 'date':
      return formatDate(value, locale);
    case 'dateRange': {
      const [from, to] = Array.isArray(value) ? value : [undefined, undefined];
      return `${formatDate(from, locale)} – ${formatDate(to, locale)}`;
    }
    default:
      return String(value ?? '');
  }
}

function formatDate(value: unknown, locale: string): string {
  const date = toDate(value);
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
      date
    );
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function toDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  const date = new Date(value as string);
  return isNaN(date.getTime()) ? undefined : date;
}
